import { downloadBlob } from './csv-export.util';

/**
 * Styled .xlsx export.
 *
 * WHY THIS EXISTS ALONGSIDE `downloadXlsx`:
 * `downloadXlsx` (csv-export.util.ts) uses the SheetJS **community** build, which
 * silently discards cell fills, autofilters and data validation — it writes the
 * file without them and without an error. Anything that needs a coloured header,
 * filter buttons, dropdowns or an embedded logo has to go through exceljs, which
 * is lazy-imported here so the ~950KB bundle only loads when someone exports.
 *
 * `downloadXlsx` stays for the plain "dump these rows" exports (bulk-import
 * templates and so on) that don't need styling.
 *
 * The sheet can start below row 1: pass `headerRow` plus `titleCells`/`images`
 * to reproduce a branded masthead above the table (see `leads-export.util.ts`,
 * which mirrors the sales team's own workbook).
 */

export interface StyledSheetColumn {
    header: string;
    /** Column width in Excel character units. */
    width?: number;
    /**
     * Values offered as a dropdown on every data cell of this column, written as
     * an Excel data-validation list (Google Sheets renders it as a dropdown too).
     */
    options?: readonly string[];
    /** Excel number format applied to data cells, e.g. `#,##0`. */
    numFmt?: string;
    align?: 'left' | 'center' | 'right';
    wrap?: boolean;
    bold?: boolean;
    /**
     * Right-align cells whose text is Arabic/Hebrew script, the way Google Sheets
     * auto-detects direction per cell. Latin values in the same column keep the
     * column's own alignment.
     */
    autoRtl?: boolean;
    /**
     * Fill this column with `=ROW()-<headerRow>` instead of the caller's values,
     * so the row numbering re-flows when someone deletes or sorts rows. The
     * reference sheet uses `SEQUENCE()` for this; `ROW()` needs no dynamic-array
     * support, so it also works in older Excel and LibreOffice.
     */
    rowNumber?: boolean;
    /**
     * Draw this column OUTSIDE the table frame — a bare rule on its left and no
     * other border, the way the leads sheet keeps its row numbers in the margin.
     * Independent of `rowNumber`: the properties report numbers its rows inside a
     * fully bordered first column.
     */
    gutter?: boolean;
}

export interface StyledSheetTheme {
    /** 6-digit hex, with or without a leading '#'. */
    headerBg: string;
    headerText: string;
    /** Light tint behind every other data row. Empty string = no banding. */
    bandBg: string;
    /** Horizontal rule between data rows, and the default for every other rule. */
    gridline: string;
    /** Outer left/right edge of the table. Defaults to `gridline`. */
    edge?: string;
    /** Vertical rule between data columns. Defaults to `gridline`. */
    columnLine?: string;
    /** Vertical rule between header cells. Defaults to `gridline`. */
    headerLine?: string;
    /** Rule under the header row. Defaults to `gridline`. */
    headerBottomLine?: string;
}

/** Dark-grey header on light-green gridlines — the sales team's own sheet. */
export const NIBNI_SHEET_THEME: StyledSheetTheme = {
    headerBg: '434343',
    headerText: 'FFFFFF',
    bandBg: '',
    gridline: 'C8E6C9',
    edge: '000000'
};

/** A run of text inside a masthead cell, so `Project : ` can be bold and the value not. */
export interface StyledSheetTitleRun {
    text: string;
    bold?: boolean;
}

/** A cell (`'A3'`) or a merged range (`'A1:C2'`) in the masthead above the table. */
export interface StyledSheetTitleCell {
    ref: string;
    text?: string;
    /** Mixed-weight content. Takes precedence over `text`. */
    richText?: readonly StyledSheetTitleRun[];
    bold?: boolean;
    align?: 'left' | 'center' | 'right';
    vertical?: 'top' | 'middle' | 'bottom';
    fontSize?: number;
}

/** A picture anchored to one cell, sized in CSS pixels. */
export interface StyledSheetImage {
    data: ArrayBuffer | Uint8Array;
    extension: 'png' | 'jpeg' | 'gif';
    /** 1-based cell the top-left corner sits in. */
    column: number;
    row: number;
    /** Pixel nudge inside that cell. */
    offsetX?: number;
    offsetY?: number;
    width: number;
    height: number;
}

export interface StyledSheetOptions {
    theme?: StyledSheetTheme;
    /** Sheet row holding the column headers. Data starts on the next row. Default 1. */
    headerRow?: number;
    /** Keep the header row visible while scrolling. Default false. */
    freezeHeader?: boolean;
    /** Excel's own grey grid behind the sheet. Default true (Excel's own default). */
    showGridLines?: boolean;
    /** Filter button on every header cell. Default true. */
    autoFilter?: boolean;
    /** Light tint behind every other data row. Default true, ignored without `theme.bandBg`. */
    bandRows?: boolean;
    /** Masthead text above the table. */
    titleCells?: readonly StyledSheetTitleCell[];
    /** Logos and other pictures. */
    images?: readonly StyledSheetImage[];
    /** Explicit row heights, keyed by 1-based sheet row. */
    rowHeights?: Readonly<Record<number, number>>;
    defaultColWidth?: number;
    defaultRowHeight?: number;
    /** Header row height. Default 24. */
    headerHeight?: number;
    /** Point size for the header and data cells. Default: the workbook's own 11. */
    fontSize?: number;
    /**
     * Rows of empty-but-styled table below the data, so the dropdowns and borders
     * are already there when the team types more leads in.
     */
    blankRows?: number;
    /**
     * Per-cell background override, returning a 6-digit hex or null to leave the
     * banding alone. `rowIndex` is 0-based over the data rows.
     */
    cellFill?: (rowIndex: number, colIndex: number, value: unknown) => string | null;
}

/** Excel caps an inline data-validation list at 255 characters, quotes included. */
const INLINE_LIST_MAX_LENGTH = 255;

/** Arabic, Hebrew, Syriac, Thaana and the Arabic supplements/presentation forms. */
const RTL_SCRIPT_RE = /[֐-׿؀-ۿ܀-ݏހ-޿ࢠ-ࣿיִ-﷿ﹰ-﻿]/;

/** One EMU-per-pixel factor; Office fixes it at 96 DPI. */
const EMU_PER_PIXEL = 9525;

function toArgb(hex: string): string {
    const clean = String(hex || '').replace('#', '').trim().toUpperCase();
    if (/^[0-9A-F]{8}$/.test(clean)) return clean;
    if (/^[0-9A-F]{6}$/.test(clean)) return `FF${clean}`;
    return 'FF000000';
}

function solidFill(hex: string): any {
    return { type: 'pattern', pattern: 'solid', fgColor: { argb: toArgb(hex) } };
}

function thinSide(hex: string): any {
    return { style: 'thin', color: { argb: toArgb(hex) } };
}

/**
 * The reference sheets' frames, reproduced exactly. Both use the same shape and
 * differ only in which rules are dark:
 *   - a `gutter` column is a bare rule on its left, outside the table;
 *   - the header row has no top edge, and the last body row closes with the edge
 *     colour;
 *   - the outer left/right edge uses `theme.edge`, inner verticals use
 *     `theme.columnLine` (body) or `theme.headerLine` (header), and the rule under
 *     the header uses `theme.headerBottomLine` — each defaulting to `gridline`,
 *     which is what makes the leads sheet a single-colour grid and the properties
 *     report a black-ruled table on near-white row separators.
 */
function cellBorder(
    theme: StyledSheetTheme,
    colIndex: number,
    lastIndex: number,
    position: { gutter?: boolean; header?: boolean; lastBodyRow?: boolean } = {}
): any {
    const grid = thinSide(theme.gridline);
    const edge = thinSide(theme.edge || theme.gridline);
    if (position.gutter) return { left: edge };
    const inner = position.header
        ? thinSide(theme.headerLine || theme.gridline)
        : thinSide(theme.columnLine || theme.gridline);
    return {
        ...(position.header ? {} : { top: grid }),
        bottom: position.header
            ? thinSide(theme.headerBottomLine || theme.gridline)
            : (position.lastBodyRow ? edge : grid),
        left: colIndex === 0 ? edge : inner,
        right: colIndex === lastIndex ? edge : inner
    };
}

/**
 * An inline list is ONE comma-joined string, so a value containing a comma or a
 * double quote would split the list or terminate it early. Rather than emit a
 * dropdown that is quietly missing options, drop the validation entirely — a
 * missing dropdown is visible, a truncated one is not.
 */
function listValidation(options: readonly string[]): any | null {
    const values = options.map((option) => String(option ?? '').trim()).filter(Boolean);
    if (!values.length) return null;

    const unusable = values.find((value) => /["',]/.test(value));
    if (unusable) {
        console.warn(`[styled-workbook] dropdown skipped: "${unusable}" contains a comma or quote`);
        return null;
    }

    const formula = `"${values.join(',')}"`;
    if (formula.length > INLINE_LIST_MAX_LENGTH) {
        console.warn(`[styled-workbook] dropdown skipped: list is ${formula.length} chars (max ${INLINE_LIST_MAX_LENGTH})`);
        return null;
    }

    return {
        type: 'list',
        allowBlank: true,
        formulae: [formula],
        showErrorMessage: true,
        // 'warning' lets someone type a value outside the list after confirming,
        // rather than hard-blocking a paste from another sheet.
        errorStyle: 'warning',
        errorTitle: 'Not in the list',
        error: `Suggested values: ${values.join(', ')}`
    };
}

function normalizeCellValue(value: unknown): string | number | boolean | Date | null {
    if (value === null || value === undefined) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'boolean') return value;
    const text = typeof value === 'string' ? value : String(value);
    return text.length ? text : null;
}

function horizontalAlignment(column: StyledSheetColumn, value: unknown): 'left' | 'center' | 'right' {
    if (column.autoRtl && typeof value === 'string' && RTL_SCRIPT_RE.test(value)) return 'right';
    return column.align ?? 'left';
}

/** 1 → 'A', 27 → 'AA'. */
function columnLetter(index: number): string {
    let remaining = Math.max(1, Math.floor(index));
    let letters = '';
    while (remaining > 0) {
        const rest = (remaining - 1) % 26;
        letters = String.fromCharCode(65 + rest) + letters;
        remaining = Math.floor((remaining - 1) / 26);
    }
    return letters;
}

async function loadExcelJs(): Promise<any> {
    const mod: any = await import('exceljs');
    // The browser build is UMD, so depending on interop the namespace is either
    // the library itself or wrapped in `.default`.
    return mod?.Workbook ? mod : mod?.default ?? mod;
}

function applyTitleCells(sheet: any, titleCells: readonly StyledSheetTitleCell[]): void {
    titleCells.forEach((title) => {
        const ref = String(title?.ref || '').trim();
        if (!ref) return;
        if (ref.includes(':')) sheet.mergeCells(ref);
        const cell = sheet.getCell(ref.split(':')[0]);
        const size = title.fontSize ?? 11;
        if (title.richText?.length) {
            cell.value = {
                richText: title.richText.map((run) => ({
                    font: { size, bold: run.bold === true },
                    text: String(run.text ?? '')
                }))
            };
        } else if (title.text !== undefined) {
            cell.value = normalizeCellValue(title.text);
        }
        // A rich-text cell carries the weight per run, so the cell-level font must
        // not force bold over all of it.
        cell.font = { bold: title.bold ?? !title.richText?.length, size };
        cell.alignment = {
            horizontal: title.align ?? 'left',
            vertical: title.vertical ?? 'middle',
            wrapText: false
        };
    });
}

function applyImages(workbook: any, sheet: any, images: readonly StyledSheetImage[]): void {
    images.forEach((image) => {
        if (!image?.data) return;
        const imageId = workbook.addImage({
            buffer: image.data as any,
            extension: image.extension || 'png'
        });
        // ⚠️ The anchor MUST be given as `nativeCol`/`nativeRow`, not `col`/`row`.
        // exceljs's Anchor constructor tests `nativeCol !== undefined` FIRST; the
        // `col` branch instead does `this.col = col`, whose setter recomputes
        // nativeColOff from the FRACTIONAL part of col — so an integer `col` plus a
        // `nativeColOff` silently writes colOff="0" and the nudge disappears.
        sheet.addImage(imageId, {
            tl: {
                nativeCol: Math.max(0, (image.column || 1) - 1),
                nativeRow: Math.max(0, (image.row || 1) - 1),
                nativeColOff: Math.round((image.offsetX || 0) * EMU_PER_PIXEL),
                nativeRowOff: Math.round((image.offsetY || 0) * EMU_PER_PIXEL)
            },
            ext: { width: image.width, height: image.height },
            editAs: 'oneCell'
        });
    });
}

export async function buildStyledWorkbookBuffer(
    sheetName: string,
    columns: StyledSheetColumn[],
    rows: unknown[][],
    options: StyledSheetOptions = {}
): Promise<ArrayBuffer> {
    const ExcelJS = await loadExcelJs();
    const theme = options.theme ?? NIBNI_SHEET_THEME;
    const headerRow = Math.max(1, Math.floor(options.headerRow ?? 1));
    const freezeHeader = options.freezeHeader === true;
    const autoFilter = options.autoFilter !== false;
    const bandRows = options.bandRows !== false && Boolean(theme.bandBg);
    const blankRows = Math.max(0, Math.floor(options.blankRows ?? 0));
    const fontSize = options.fontSize;
    const lastIndex = columns.length - 1;

    const view: any = {
        ...(freezeHeader ? { state: 'frozen', ySplit: headerRow } : {}),
        ...(options.showGridLines === false ? { showGridLines: false } : {})
    };

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'nibni';
    const sheet = workbook.addWorksheet(sheetName || 'Sheet1', {
        views: Object.keys(view).length ? [view] : undefined,
        properties: {
            ...(options.defaultColWidth ? { defaultColWidth: options.defaultColWidth } : {}),
            ...(options.defaultRowHeight ? { defaultRowHeight: options.defaultRowHeight } : {})
        }
    });

    // Widths only — `sheet.columns = [{ header }]` would write the headers into
    // row 1, which is the masthead when `headerRow` is further down.
    columns.forEach((column, index) => {
        if (column.width) sheet.getColumn(index + 1).width = column.width;
    });

    if (options.titleCells?.length) applyTitleCells(sheet, options.titleCells);
    if (options.images?.length) applyImages(workbook, sheet, options.images);

    Object.entries(options.rowHeights || {}).forEach(([row, height]) => {
        const index = Number(row);
        if (Number.isFinite(index) && index >= 1 && height) sheet.getRow(index).height = height;
    });

    const header = sheet.getRow(headerRow);
    if (options.headerHeight) header.height = options.headerHeight;
    columns.forEach((column, index) => {
        const cell = header.getCell(index + 1);
        cell.value = normalizeCellValue(column.header);
        cell.fill = solidFill(theme.headerBg);
        cell.font = { bold: true, size: fontSize ?? 11, color: { argb: toArgb(theme.headerText) } };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.border = cellBorder(theme, index, lastIndex, { gutter: column.gutter, header: true });
    });
    header.commit();

    const totalBodyRows = rows.length + blankRows;
    for (let rowIndex = 0; rowIndex < totalBodyRows; rowIndex += 1) {
        const source = rows[rowIndex];
        const sheetRowNumber = headerRow + 1 + rowIndex;
        const sheetRow = sheet.getRow(sheetRowNumber);
        const banded = bandRows && rowIndex % 2 === 0;
        const lastBodyRow = rowIndex === totalBodyRows - 1;

        columns.forEach((column, colIndex) => {
            const raw = source ? source[colIndex] : null;
            const cell = sheetRow.getCell(colIndex + 1);

            if (column.rowNumber) {
                // Only number rows that actually carry a record — a trailing blank
                // row numbered "103" reads as a missing one.
                cell.value = source ? { formula: `ROW()-${headerRow}` } : null;
            } else {
                cell.value = normalizeCellValue(raw);
            }

            const override = options.cellFill?.(rowIndex, colIndex, raw) ?? null;
            if (override) cell.fill = solidFill(override);
            else if (banded) cell.fill = solidFill(theme.bandBg);

            cell.border = cellBorder(theme, colIndex, lastIndex, { gutter: column.gutter, lastBodyRow });
            cell.alignment = {
                vertical: 'middle',
                horizontal: horizontalAlignment(column, raw),
                wrapText: Boolean(column.wrap)
            };
            if (fontSize || column.bold) {
                cell.font = { ...(fontSize ? { size: fontSize } : {}), ...(column.bold ? { bold: true } : {}) };
            }
            if (column.numFmt) cell.numFmt = column.numFmt;
        });
        sheetRow.commit();
    }

    // ONE validation per column over the whole body range, the way the reference
    // sheet does it — per-cell entries would repeat the list in the XML once per
    // row and blow the file up.
    if (totalBodyRows > 0) {
        columns.forEach((column, colIndex) => {
            if (!column.options?.length) return;
            const validation = listValidation(column.options);
            if (!validation) return;
            const letter = columnLetter(colIndex + 1);
            sheet.dataValidations.add(
                `${letter}${headerRow + 1}:${letter}${headerRow + totalBodyRows}`,
                validation
            );
        });
    }

    if (autoFilter && columns.length) {
        sheet.autoFilter = {
            from: { row: headerRow, column: 1 },
            to: { row: headerRow + totalBodyRows, column: columns.length }
        };
    }

    return workbook.xlsx.writeBuffer();
}

export async function downloadStyledXlsx(
    filename: string,
    sheetName: string,
    columns: StyledSheetColumn[],
    rows: unknown[][],
    options: StyledSheetOptions = {}
): Promise<void> {
    const buffer = await buildStyledWorkbookBuffer(sheetName, columns, rows, options);
    downloadBlob(
        new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        filename
    );
}

/**
 * Excel stores a date as a day count from 1899-12-30 with no timezone, so a
 * local-midnight `Date` in a negative-offset zone lands on the previous day.
 * Pinning the local calendar date to UTC midnight makes the serial land on the
 * day the user actually sees in the app.
 */
export function toSheetDate(value: unknown): Date | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()));
}

export interface SheetImageSource {
    data: ArrayBuffer;
    extension: 'png' | 'jpeg' | 'gif';
    width: number;
    height: number;
}

/** exceljs writes these three; anything else has to be skipped, not guessed at. */
function imageExtension(mimeType: string): 'png' | 'jpeg' | 'gif' | null {
    const type = String(mimeType || '').toLowerCase();
    if (type.includes('png')) return 'png';
    if (type.includes('jpeg') || type.includes('jpg')) return 'jpeg';
    if (type.includes('gif')) return 'gif';
    return null;
}

async function intrinsicSize(blob: Blob): Promise<{ width: number; height: number } | null> {
    if (typeof createImageBitmap === 'function') {
        try {
            const bitmap = await createImageBitmap(blob);
            const size = { width: bitmap.width, height: bitmap.height };
            bitmap.close?.();
            return size;
        } catch {
            // fall through to the <img> path
        }
    }
    if (typeof Image !== 'function' || typeof URL?.createObjectURL !== 'function') return null;
    const url = URL.createObjectURL(blob);
    try {
        return await new Promise<{ width: number; height: number } | null>((resolve) => {
            const image = new Image();
            image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
            image.onerror = () => resolve(null);
            image.src = url;
        });
    } finally {
        URL.revokeObjectURL(url);
    }
}

/**
 * Read an image and scale it to fit a pixel box without distorting it, ready to
 * hand to `StyledSheetImage`. Returns null — never throws — for an unreachable
 * URL or an unsupported format, because a missing logo must not fail an export.
 */
export async function loadSheetImage(
    source: Blob | string,
    box: { maxWidth: number; maxHeight: number }
): Promise<SheetImageSource | null> {
    try {
        const blob = typeof source === 'string' ? await (await fetch(source)).blob() : source;
        const extension = imageExtension(blob.type);
        if (!extension) return null;

        const data = await blob.arrayBuffer();
        const size = await intrinsicSize(blob);
        if (!size?.width || !size?.height) return null;

        const scale = Math.min(box.maxWidth / size.width, box.maxHeight / size.height, 1);
        return {
            data,
            extension,
            width: Math.max(1, Math.round(size.width * scale)),
            height: Math.max(1, Math.round(size.height * scale))
        };
    } catch {
        return null;
    }
}

/**
 * First of several candidate sources that yields a usable image. Built assets live
 * under the Catalyst mount in production and at the root under `ng serve`, so a
 * caller that needs the bytes has to try both.
 */
export async function loadFirstSheetImage(
    sources: readonly (Blob | string)[],
    box: { maxWidth: number; maxHeight: number }
): Promise<SheetImageSource | null> {
    for (const source of sources) {
        const loaded = await loadSheetImage(source, box);
        if (loaded) return loaded;
    }
    return null;
}
